// 👨‍💼 Staff Dashboard - لوحة الموظفين لإدارة التذاكر
import React, { useState, useEffect } from 'react';
import { 
  Ticket, Clock, AlertTriangle, CheckCircle, User, Calendar, 
  Filter, Search, Eye, MessageSquare, Phone, Mail, 
  TrendingUp, BarChart3, Users, Target
} from 'lucide-react';
import { getAllComplaints, getComplaintsStats, updateComplaintStatus, assignComplaintToEmployee, initializeCSVService, refreshComplaintsData } from '../services/csvComplaintsService';

const StaffDashboard = () => {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [staffStats, setStaffStats] = useState({
    total: 0,
    newTickets: 0,
    inProgress: 0,
    resolved: 0,
    urgent: 0
  });
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());

  // تحميل البيانات الحقيقية
  const loadData = async () => {
    await initializeCSVService();
    const realTickets = getAllComplaints();
    const stats = getComplaintsStats();
    
    console.log('📊 إحصائيات الشكاوى:', stats);
    setStaffStats(stats);
  };

  useEffect(() => {
    // تهيئة خدمة CSV ثم تحميل البيانات
    const initializeAndLoad = async () => {
      await initializeCSVService();
      loadData();
    };
    
    initializeAndLoad();
    
    // الاستماع لتحديثات الشكاوى
    const handleComplaintsUpdate = async (event) => {
      console.log('🔔 تم استلام إشعار تحديث في لوحة الموظفين:', event.detail);
      console.log('🔄 بدء إعادة تحميل البيانات...');
      
      // تأخير قصير للتأكد من اكتمال الحفظ
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // إعادة تهيئة الخدمة أولاً
      await initializeCSVService();
      
      // إعادة تحميل البيانات والتذاكر
      await loadData();
      
      // إعادة تحميل التذاكر أيضاً
      const complaints = getAllComplaints();
      const realTickets = complaints.map(complaint => ({
        id: complaint.ticket_id || 'غير محدد',
        title: `${complaint.main_category || 'غير محدد'} - ${complaint.sub_category || 'غير محدد'}`,
        description: (complaint.complaint_text || 'لا يوجد وصف').substring(0, 100) + '...',
        citizen: {
          name: complaint.citizen_name || 'غير محدد',
          phone: complaint.phone || 'غير محدد',
          email: complaint.email || 'غير محدد',
          id: complaint.user_id || complaint.complaint_id || 'غير محدد'
        },
        priority: mapPriority(complaint.priority_level || 'منخفض'),
        status: mapStatus(complaint.status || 'جديد'),
        category: complaint.sector || 'غير محدد',
        subcategory: complaint.sub_category || 'غير محدد',
        riskLevel: complaint.is_violence_related === 'نعم' ? 98 : 45,
        previousComplaints: 0,
        estimatedTime: getEstimatedTime(complaint.priority_level || 'منخفض'),
        createdAt: complaint.created_at ? new Date(complaint.created_at) : new Date(),
        assignedTo: complaint.responsible_employee || 'غير مُعيَّن',
        channel: complaint.channel || 'غير محدد',
        aiAnalysis: {
          intent: `${complaint.main_category || 'غير محدد'}`,
          sentiment: complaint.is_violence_related === 'نعم' ? 'خوف وقلق شديد' : 'طلب مساعدة',
          keywords: extractKeywords(complaint.complaint_text || '')
        }
      }));
      
      setTickets(realTickets);
      console.log('🔄 تم تحديث التذاكر:', realTickets.length);
    };
    
    window.addEventListener('complaintsUpdated', handleComplaintsUpdate);
    
    // تحديث تلقائي كل 10 ثواني
    const interval = setInterval(async () => {
      console.log('🔄 تحديث تلقائي للبيانات...');
      
      // تحقق من وجود تحديثات في localStorage
      const lastUpdated = localStorage.getItem('csv_last_updated');
      const autoUpdatedCSV = localStorage.getItem('auto_updated_csv');
      
      if (lastUpdated && autoUpdatedCSV) {
        const updateTime = new Date(lastUpdated);
        const now = new Date();
        const timeDiff = now - updateTime;
        
        // إذا كان هناك تحديث خلال آخر دقيقة
        if (timeDiff < 60000) {
          console.log('📊 تم اكتشاف تحديث جديد، إعادة تحميل البيانات...');
          await handleRefreshData();
          setLastUpdateTime(new Date());
        }
      }
    }, 10000);
    
    return () => {
      window.removeEventListener('complaintsUpdated', handleComplaintsUpdate);
      clearInterval(interval);
    };
  }, []);

  // تحويل البيانات الحقيقية لتنسيق التذاكر
  useEffect(() => {
    const loadTickets = async () => {
      // تأكد من تهيئة الخدمة أولاً
      await initializeCSVService();
      
      const complaints = getAllComplaints();
      console.log('📋 تحميل الشكاوى:', complaints.length);
      console.log('📊 أول شكوى:', complaints[0]);
      
      if (complaints.length === 0) {
        console.log('⚠️ لا توجد شكاوى - استخدام البيانات الافتراضية');
        return;
      }
      
      const realTickets = complaints.map(complaint => {
        console.log('🔄 معالجة شكوى:', complaint.ticket_id, complaint.citizen_name);
        return {
          id: complaint.ticket_id || 'غير محدد',
          title: `${complaint.main_category || 'غير محدد'} - ${complaint.sub_category || 'غير محدد'}`,
          description: (complaint.complaint_text || 'لا يوجد وصف').substring(0, 100) + '...',
          citizen: {
            name: complaint.citizen_name || 'غير محدد',
            phone: complaint.phone || 'غير محدد',
            email: complaint.email || 'غير محدد',
            id: complaint.user_id || complaint.complaint_id || 'غير محدد'
          },
          priority: mapPriority(complaint.priority_level || 'منخفض'),
          status: mapStatus(complaint.status || 'جديد'),
          category: complaint.sector || 'غير محدد',
          subcategory: complaint.sub_category || 'غير محدد',
          riskLevel: complaint.is_violence_related === 'نعم' ? 98 : 45,
          previousComplaints: 0,
          estimatedTime: getEstimatedTime(complaint.priority_level || 'منخفض'),
          createdAt: complaint.created_at ? new Date(complaint.created_at) : new Date(),
          assignedTo: complaint.responsible_employee || 'غير مُعيَّن',
          channel: complaint.channel || 'غير محدد',
          aiAnalysis: {
            intent: `${complaint.main_category || 'غير محدد'}`,
            sentiment: complaint.is_violence_related === 'نعم' ? 'خوف وقلق شديد' : 'طلب مساعدة',
            keywords: extractKeywords(complaint.complaint_text || '')
          }
        };
      });

      console.log('🎯 التذاكر المحولة:', realTickets.length);
      console.log('📊 أول تذكرة:', realTickets[0]);
      setTickets(realTickets);
      
      // تحديث الإحصائيات بعد تحميل التذاكر
      const stats = getComplaintsStats();
      console.log('📊 إحصائيات محدثة:', stats);
      setStaffStats(stats);
    };

    loadTickets();
  }, []); // تحميل مرة واحدة فقط

  // دوال مساعدة لتحويل البيانات
  const mapPriority = (priority) => {
    const mapping = {
      'طارئ': 'urgent',
      'عالي': 'high', 
      'متوسط': 'medium',
      'منخفض': 'low'
    };
    return mapping[priority] || 'medium';
  };

  const mapStatus = (status) => {
    const mapping = {
      'جديد': 'new',
      'قيد المعالجة': 'in_progress',
      'مكتمل': 'resolved',
      'مغلق': 'closed'
    };
    return mapping[status] || 'new';
  };

  const getEstimatedTime = (priority) => {
    const timeMapping = {
      'طارئ': '30 ثانية',
      'عالي': '2 ساعة',
      'متوسط': '24 ساعة',
      'منخفض': '48 ساعة'
    };
    return timeMapping[priority] || '24 ساعة';
  };

  const extractKeywords = (text) => {
    const keywords = [];
    if (text.includes('عنف')) keywords.push('عنف');
    if (text.includes('راتب')) keywords.push('راتب');
    if (text.includes('رخصة')) keywords.push('رخصة');
    if (text.includes('أبشر')) keywords.push('أبشر');
    return keywords.slice(0, 4);
  };

  // فلترة التذاكر
  useEffect(() => {
    let filtered = tickets;

    // فلترة حسب الحالة
    if (selectedFilter !== 'all') {
      filtered = filtered.filter(ticket => ticket.status === selectedFilter);
    }

    // فلترة حسب البحث
    if (searchTerm) {
      filtered = filtered.filter(ticket => 
        ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ticket.citizen && ticket.citizen.name && ticket.citizen.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        ticket.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredTickets(filtered);
  }, [tickets, selectedFilter, searchTerm]);

  // الحصول على لون الأولوية
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // الحصول على لون الحالة
  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // تحديث حالة التذكرة
  const updateTicketStatus = (ticketId, newStatus) => {
    setTickets(prev => prev.map(ticket => 
      ticket.id === ticketId 
        ? { ...ticket, status: newStatus, assignedTo: newStatus === 'in_progress' ? 'أنت' : ticket.assignedTo }
        : ticket
    ));
  };

  // تعيين التذكرة للموظف
  const assignTicket = (ticketId) => {
    setTickets(prev => prev.map(ticket => 
      ticket.id === ticketId 
        ? { ...ticket, assignedTo: 'أنت', status: 'in_progress' }
        : ticket
    ));
  };

  // إعادة تحميل البيانات يدوياً
  const handleRefreshData = async () => {
    console.log('🔄 المستخدم طلب إعادة تحميل البيانات...');
    try {
      await refreshComplaintsData();
      
      // إعادة تحميل التذاكر
      const complaints = getAllComplaints();
      const realTickets = complaints.map(complaint => ({
        id: complaint.ticket_id || 'غير محدد',
        title: `${complaint.main_category || 'غير محدد'} - ${complaint.sub_category || 'غير محدد'}`,
        description: (complaint.complaint_text || 'لا يوجد وصف').substring(0, 100) + '...',
        citizen: {
          name: complaint.citizen_name || 'غير محدد',
          phone: complaint.phone || 'غير محدد',
          email: complaint.email || 'غير محدد',
          id: complaint.user_id || complaint.complaint_id || 'غير محدد'
        },
        priority: mapPriority(complaint.priority_level || 'منخفض'),
        status: mapStatus(complaint.status || 'جديد'),
        category: complaint.sector || 'غير محدد',
        subcategory: complaint.sub_category || 'غير محدد',
        riskLevel: complaint.is_violence_related === 'نعم' ? 98 : 45,
        previousComplaints: 0,
        estimatedTime: getEstimatedTime(complaint.priority_level || 'منخفض'),
        createdAt: complaint.created_at ? new Date(complaint.created_at) : new Date(),
        assignedTo: complaint.responsible_employee || 'غير مُعيَّن',
        channel: complaint.channel || 'غير محدد',
        aiAnalysis: {
          intent: `${complaint.main_category || 'غير محدد'}`,
          sentiment: complaint.is_violence_related === 'نعم' ? 'خوف وقلق شديد' : 'طلب مساعدة',
          keywords: extractKeywords(complaint.complaint_text || '')
        }
      }));
      
      setTickets(realTickets);
      
      // تحديث الإحصائيات
      const stats = getComplaintsStats();
      setStaffStats(stats);
      
      console.log('✅ تم تحديث البيانات بنجاح:', realTickets.length);
    } catch (error) {
      console.error('❌ خطأ في إعادة تحميل البيانات:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl shadow-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">👨‍💼 لوحة الموظفين</h1>
            <p className="text-lg opacity-90">إدارة التذاكر والشكاوى</p>
          </div>
          <div className="text-right">
            <p className="text-sm opacity-75">مرحباً</p>
            <p className="text-xl font-bold">أحمد المطيري</p>
            <p className="text-sm opacity-75">موظف خدمة العملاء</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleRefreshData}
                className="px-3 py-1 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors text-sm"
              >
                🔄 تحديث البيانات
              </button>
              <div className="flex items-center gap-2 text-xs">
                <div className={`w-2 h-2 rounded-full ${autoUpdateEnabled ? 'bg-green-300 animate-pulse' : 'bg-gray-300'}`}></div>
                <span>تحديث تلقائي</span>
              </div>
              <div className="text-xs opacity-75">
                آخر تحديث: {lastUpdateTime.toLocaleTimeString('ar-SA')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="grid md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow-lg p-4 text-center">
          <Ticket className="w-8 h-8 text-blue-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-blue-600">{staffStats.total}</div>
          <div className="text-sm text-gray-600">إجمالي التذاكر</div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-4 text-center">
          <AlertTriangle className="w-8 h-8 text-red-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-red-600">{staffStats.urgent}</div>
          <div className="text-sm text-gray-600">طارئة</div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-4 text-center">
          <Clock className="w-8 h-8 text-orange-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-orange-600">{staffStats.newTickets}</div>
          <div className="text-sm text-gray-600">جديدة</div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-4 text-center">
          <TrendingUp className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-yellow-600">{staffStats.inProgress}</div>
          <div className="text-sm text-gray-600">قيد المعالجة</div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-4 text-center">
          <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-green-600">{staffStats.resolved}</div>
          <div className="text-sm text-gray-600">مكتملة</div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                selectedFilter === 'all' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              الكل ({staffStats.total || 0})
            </button>
            <button
              onClick={() => setSelectedFilter('new')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                selectedFilter === 'new' 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              جديدة ({staffStats.newTickets})
            </button>
            <button
              onClick={() => setSelectedFilter('in_progress')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                selectedFilter === 'in_progress' 
                  ? 'bg-yellow-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              قيد المعالجة ({staffStats.inProgress})
            </button>
            <button
              onClick={() => setSelectedFilter('resolved')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                selectedFilter === 'resolved' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              مكتملة ({staffStats.resolved})
            </button>
          </div>
          
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="البحث في التذاكر..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">📋 قائمة التذاكر</h2>
        </div>
        
        <div className="divide-y divide-gray-200">
          {filteredTickets.map((ticket, index) => (
            <div key={`${ticket.id}-${index}`} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-gray-800">{ticket.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority === 'urgent' ? '🚨 طارئ' :
                       ticket.priority === 'high' ? '🔴 عالي' :
                       ticket.priority === 'medium' ? '🟡 متوسط' : '🟢 منخفض'}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                      {ticket.status === 'new' ? 'جديدة' :
                       ticket.status === 'in_progress' ? 'قيد المعالجة' : 'مكتملة'}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 mb-3">{ticket.description}</p>
                  
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p><strong>رقم التذكرة:</strong> {ticket.id}</p>
                      <p><strong>المواطن:</strong> {ticket.citizen?.name || 'غير محدد'}</p>
                      <p><strong>الهاتف:</strong> {ticket.citizen?.phone || 'غير محدد'}</p>
                    </div>
                    <div>
                      <p><strong>القطاع:</strong> {ticket.category}</p>
                      <p><strong>مستوى الخطورة:</strong> {ticket.riskLevel}/100</p>
                      <p><strong>الوقت المتوقع:</strong> {ticket.estimatedTime}</p>
                    </div>
                  </div>
                  
                  {ticket.assignedTo && (
                    <p className="text-sm text-blue-600 mt-2">
                      <strong>مُعيّن لـ:</strong> {ticket.assignedTo}
                    </p>
                  )}
                </div>
                
                <div className="flex flex-col gap-2 ml-4">
                  <button
                    onClick={() => setSelectedTicket(ticket)}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    عرض
                  </button>
                  
                  {ticket.status === 'new' && (
                    <button
                      onClick={() => assignTicket(ticket.id)}
                      className="flex items-center gap-2 px-3 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                    >
                      <User className="w-4 h-4" />
                      تعيين لي
                    </button>
                  )}
                  
                  {ticket.status === 'in_progress' && ticket.assignedTo === 'أنت' && (
                    <button
                      onClick={() => updateTicketStatus(ticket.id, 'resolved')}
                      className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" />
                      إنهاء
                    </button>
                  )}
                </div>
              </div>
              
              <div className="mt-4 text-xs text-gray-500">
                تم الإنشاء: {ticket.createdAt && !isNaN(ticket.createdAt) ? ticket.createdAt.toLocaleString('ar-SA') : 'غير محدد'}
              </div>
            </div>
          ))}
        </div>
        
        {filteredTickets.length === 0 && (
          <div className="p-12 text-center">
            <Ticket className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-500 mb-2">لا توجد تذاكر</h3>
            <p className="text-gray-400">لا توجد تذاكر تطابق المعايير المحددة</p>
          </div>
        )}
      </div>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">تفاصيل التذكرة</h2>
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">{selectedTicket.title}</h3>
                <p className="text-gray-600">{selectedTicket.description}</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-gray-800 mb-3">👤 معلومات المواطن</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>الاسم:</strong> {selectedTicket.citizen?.name || 'غير محدد'}</p>
                    <p><strong>الهوية:</strong> {selectedTicket.citizen?.id || 'غير محدد'}</p>
                    <p><strong>الهاتف:</strong> {selectedTicket.citizen?.phone || 'غير محدد'}</p>
                    <p><strong>البريد:</strong> {selectedTicket.citizen?.email || 'غير محدد'}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-bold text-gray-800 mb-3">📋 تفاصيل التذكرة</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>رقم التذكرة:</strong> {selectedTicket.id}</p>
                    <p><strong>القطاع:</strong> {selectedTicket.category}</p>
                    <p><strong>الفئة الفرعية:</strong> {selectedTicket.subcategory}</p>
                    <p><strong>مستوى الخطورة:</strong> {selectedTicket.riskLevel}/100</p>
                    <p><strong>الشكاوى السابقة:</strong> {selectedTicket.previousComplaints}</p>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-bold text-gray-800 mb-3">🤖 تحليل الذكاء الاصطناعي</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  <p><strong>القصد:</strong> {selectedTicket.aiAnalysis.intent}</p>
                  <p><strong>المشاعر:</strong> {selectedTicket.aiAnalysis.sentiment}</p>
                  <p><strong>الكلمات المفتاحية:</strong> {selectedTicket.aiAnalysis.keywords.join(', ')}</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                  <Phone className="w-4 h-4" />
                  اتصال
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors">
                  <MessageSquare className="w-4 h-4" />
                  رسالة
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors">
                  <Mail className="w-4 h-4" />
                  إيميل
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffDashboard;
